<script setup lang="ts">
type Props = {
  to?: string;
  isButton?: boolean;
  isActive?: boolean;
  target?: string;
  glowColor?: string;
};

const { to, isButton, isActive, target, glowColor } = defineProps<Props>();

const { closeMenu } = useLayoutStore();
const { isLayoutMobileOrTablet, isSidebarMenuOpen } = useLayout();

function onMenuClick() {
  if (isLayoutMobileOrTablet.value) {
    closeMenu();
  }
}

const glowStyle = computed(() =>
  glowColor
    ? ({ '--glow-color': glowColor } as Record<string, string>)
    : undefined,
);
</script>

<template>
  <button
    v-if="isButton"
    :class="[
      $style.menuLink,
      { [$style.isClosed]: !isSidebarMenuOpen },
      { [$style.isActive]: isActive },
      { [$style.hasGlow]: glowColor },
    ]"
    :style="glowStyle"
    @click="onMenuClick"
  >
    <slot />
  </button>

  <NuxtLink
    v-else-if="to"
    :class="[
      $style.menuLink,
      { [$style.isClosed]: !isSidebarMenuOpen },
      { [$style.isActive]: isActive },
      { [$style.hasGlow]: glowColor },
    ]"
    :external="target === '_blank'"
    no-prefetch
    :style="glowStyle"
    :target="target"
    :to="to"
    @click="onMenuClick"
  >
    <slot />
  </NuxtLink>
</template>

<style module lang="scss">
.menuLink {
  @apply flex justify-start items-center text-white text-[14px] leading-normal transition-none font-semibold cursor-pointer relative overflow-hidden;
  background-color: rgba(255, 255, 255, 0.05);
  width: 100%;
  height: 32px;
  padding: 0 10px;
  gap: 9px;
  border-radius: 12px;
  border: 1px solid transparent;

  svg {
    @apply w-[20px] h-[20px] min-w-[20px] relative z-10;
  }

  img,
  [class*='sidebarIcon'] {
    @apply w-[20px] h-[20px] relative z-10;
  }

  span {
    @apply relative z-10 whitespace-nowrap;
  }

  &.isActive {
    background-color: rgba(20, 20, 221, 0.2);
    border-color: #1414dd;
  }

  @media (hover: hover) {
    &:hover:not(.isActive) {
      background-color: rgba(255, 255, 255, 0.1);
    }
  }

  @media screen and (min-width: 1024px) {
    /* Rail retraído segue a mesma linguagem do item aberto (fundo
       translúcido, raio 12, ativo azul) — só vira um quadrado 48x48. */
    &.isClosed {
      @apply w-[48px] h-[48px] flex justify-center items-center mb-1.5 mx-auto px-0;

      span:not([class*='sidebarIcon']):not([class*='spinner']) {
        @apply hidden;
      }

      svg {
        @apply w-[20px] h-[20px] min-w-[20px];
      }

      img,
      [class*='sidebarIcon'] {
        @apply w-[20px] h-[20px] opacity-100;
      }
    }
  }
}
</style>
